import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * After Stripe redirects back from a successful Checkout, we poll the
 * /billing/checkout/status endpoint until the backend confirms the circle
 * has been provisioned (which happens either via webhook OR via the
 * endpoint's own provisioning attempt). Then we navigate to the group page.
 */
const POLL_INTERVAL_MS = 1800;
const MAX_ATTEMPTS = 30; // ~54 seconds

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | provisioned | failed
  const [group, setGroup] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.status === "provisioned" && data.group) {
          setGroup(data.group);
          setStatus("provisioned");
          // Auto-redirect after a short celebratory pause.
          setTimeout(() => navigate(`/groups/${data.group.id}`), 1800);
          return;
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setStatus("failed");
          toast.error("Couldn't find your checkout session.");
          return;
        }
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-ivory grain-bg">
      <TopNav />
      <main className="max-w-2xl mx-auto px-5 md:px-10 py-20 text-center" data-testid="billing-success-page">
        {status === "checking" && (
          <>
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 text-gold animate-pulse mb-6">
              <Sparkle size={28} weight="duotone" />
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-forest">Finishing your setup…</h1>
            <p className="text-sageGray mt-4 max-w-md mx-auto leading-relaxed">
              Stripe accepted your card and started your 30-day trial. We're now creating your circle. Hang tight — this usually takes a couple of seconds.
            </p>
          </>
        )}
        {status === "provisioned" && group && (
          <>
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest text-gold mb-6">
              <CheckCircle size={32} weight="fill" />
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-forest" data-testid="success-headline">Welcome to {group.name}!</h1>
            <p className="text-sageGray mt-4 max-w-md mx-auto leading-relaxed">
              Your free 30-day trial has begun. We'll redirect you to your circle in a moment.
            </p>
            <Button onClick={() => navigate(`/groups/${group.id}`)} className="mt-8 bg-forest hover:bg-forestSoft text-ivory rounded-full px-7" data-testid="success-goto-circle">
              Go to your circle
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-forest" data-testid="success-failed-headline">We couldn't confirm your payment</h1>
            <p className="text-sageGray mt-4 max-w-md mx-auto leading-relaxed">
              Your payment may still be processing on Stripe's side. Refresh in a moment, or head back to the dashboard and try again.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-8 bg-forest hover:bg-forestSoft text-ivory rounded-full px-7" data-testid="success-back-dashboard">Back to dashboard</Button>
          </>
        )}
      </main>
    </div>
  );
}
